import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PackageSummaryResponse } from '../../api/types';
import { Columns, type Column } from '../../components/charts/Columns';
import { RankedBars, type RankedBar } from '../../components/charts/RankedBars';
import { bucketValues, daysBetween, granularityFor } from '../../components/charts/buckets';
import { bucketLabel, bucketTitle } from '../../components/charts/dates';
import { count, pounds } from '../../i18n/format';
import { useLocalisedName } from '../../i18n/localisedName';
import { magnitude } from '../catalog/money';

type Measure = 'revenue' | 'sold' | 'heldValue';

/** Where a package's units went: used and expired in the range, and what is still held today. */
const PARTS = {
  used: 'var(--chart-1)',
  held: 'var(--chart-seq-3)',
  expired: 'var(--chart-2)',
} as const;

/**
 * The Packages screen's charts (CR-36), from the package summary over the chosen period.
 *
 * Four questions. Which packages sell, and what they bring in. How much the clinic has been paid
 * for and not yet delivered — the sessions and pulses patients still hold, at the price they paid
 * before VAT: money already in the bank that is also work still owed. Where each package's units
 * go — used, expired, or still waiting. And who is about to lose what they paid for: the packages
 * expiring in the next ninety days, which is a list worth calling before it becomes a refund
 * argument.
 *
 * Sessions and pulses are never added together. The by-package chart compares packages in money
 * and in packages sold, which every package shares; the units chart is a share within each package,
 * each row in its own unit.
 */
export function PackagesOverview({
  summary,
  from,
  to,
}: {
  readonly summary: PackageSummaryResponse;
  readonly from: string;
  readonly to: string;
}) {
  const { t, i18n } = useTranslation();
  const localised = useLocalisedName();
  const [measure, setMeasure] = useState<Measure>('revenue');

  const rows = summary.byPackage ?? [];
  const bands = summary.expiring ?? [];
  const soon = bands[0];
  const name = (row: (typeof rows)[number]) =>
    row.nameEn || row.nameAr
      ? localised.name({ nameEn: row.nameEn, nameAr: row.nameAr })
      : row.id
        ? t('overview.noLongerListed')
        : t('overview.packages.noPackage');

  const granularity = granularityFor(daysBetween(from, to));
  const columns: Column[] = bucketValues(
    (summary.byDay ?? []).map((day) => ({ date: day.day ?? '', values: [day.sold ?? 0, magnitude(day.revenue)] })),
    granularity,
  ).map((bucket) => {
    const [sold = 0, revenue = 0] = bucket.values;
    return {
      key: bucket.start,
      label: bucketLabel(bucket, granularity, i18n.language),
      description: t('overview.packages.columnDescription', {
        when: bucketTitle(bucket, granularity, i18n.language, (first, last) =>
          t('overview.weekOf', { from: first, to: last }),
        ),
        count: sold,
        revenue: pounds(revenue),
      }),
      segments: [{ value: sold, colour: 'var(--chart-1)' }],
    };
  });

  const ranked: RankedBar[] = rows
    .map((row) => ({
      key: row.id ?? 'none',
      label: name(row),
      value:
        measure === 'revenue' ? magnitude(row.revenue) : measure === 'sold' ? (row.sold ?? 0) : magnitude(row.heldValue),
    }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);

  const units = rows.filter((row) => (row.used ?? 0) + (row.expired ?? 0) + (row.held ?? 0) > 0);
  const expiring: RankedBar[] = bands.map((band, index) => ({
    key: String(band.fromDays ?? index),
    label: t(`overview.packages.band.${index}`, { count: band.patients ?? 0 }),
    value: band.packages ?? 0,
    tone: index === 0 ? ('alert' as const) : undefined,
  }));

  return (
    <div className="overview">
      <section className="measures" aria-label={t('overview.packages.strip')}>
        <div className="measure measure--lead">
          <span className="measure__label">{t('overview.packages.sold')}</span>
          <span className="measure__value">{count(summary.sold ?? 0)}</span>
          <span className="measure__note">
            {t('overview.packages.revenue', { revenue: pounds(magnitude(summary.revenue)) })}
          </span>
        </div>
        <div className="measure">
          <span className="measure__label">{t('overview.packages.held', { currency: 'EGP' })}</span>
          <span className="measure__value">{count(Math.round(magnitude(summary.heldValue)))}</span>
          <span className="measure__note">{t('overview.packages.holders', { count: summary.holders ?? 0 })}</span>
        </div>
        <div className={(soon?.packages ?? 0) > 0 ? 'measure measure--alert' : 'measure'}>
          <span className="measure__label">{t('overview.packages.expiringSoon')}</span>
          <span className="measure__value">{count(soon?.packages ?? 0)}</span>
          <span className="measure__note">
            {t('overview.packages.expiringPatients', { count: soon?.patients ?? 0 })}
          </span>
        </div>
      </section>

      <div className="overview-grid">
        <section className="panel">
          <div className="panel__heading">
            <h2>{t('overview.packages.overTime')}</h2>
            <p className="muted">{t(`overview.per.${granularity}`)}</p>
          </div>
          <Columns columns={columns} label={t('overview.packages.overTime')} />
        </section>

        <section className="panel">
          <div className="panel__header">
            <div className="panel__heading">
              <h2>{t('overview.packages.byPackage')}</h2>
              <p className="muted">{t(`overview.packages.measureNote.${measure}`)}</p>
            </div>
            <div className="tabs" role="tablist" aria-label={t('overview.packages.byPackage')}>
              {(['revenue', 'sold', 'heldValue'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  role="tab"
                  aria-selected={measure === option}
                  className={measure === option ? 'tab tab--active' : 'tab'}
                  onClick={() => setMeasure(option)}
                >
                  {t(`overview.packages.view.${option}`)}
                </button>
              ))}
            </div>
          </div>
          {ranked.length > 0 ? (
            <RankedBars rows={ranked} format={measure === 'sold' ? count : pounds} firstRows={8} />
          ) : (
            <p className="muted">{t('overview.packages.nothing')}</p>
          )}
        </section>

        {units.length > 0 ? (
          <section className="panel">
            <div className="panel__heading">
              <h2>{t('overview.packages.where')}</h2>
              <p className="muted">{t('overview.packages.whereNote')}</p>
            </div>
            <ol className="bars bars--plain">
              {units.map((row) => {
                const used = row.used ?? 0;
                const expired = row.expired ?? 0;
                const held = row.held ?? 0;
                const unit = t(`overview.packages.unit.${row.kind ?? 'SESSION'}`);
                const description = t('overview.packages.whereDescription', {
                  name: name(row),
                  used: count(used),
                  expired: count(expired),
                  held: count(held),
                  unit,
                });
                return (
                  <li key={row.id ?? 'none'}>
                    <div className="bars__row has-tip" tabIndex={0} aria-label={description}>
                      <span className="bars__label" title={name(row)}>
                        {name(row)}
                      </span>
                      <span className="bars__track">
                        <span className="bars__stack" style={{ inlineSize: '100%' }}>
                          {used > 0 ? <span style={{ flexGrow: used, background: PARTS.used }} /> : null}
                          {expired > 0 ? <span style={{ flexGrow: expired, background: PARTS.expired }} /> : null}
                          {held > 0 ? <span style={{ flexGrow: held, background: PARTS.held }} /> : null}
                        </span>
                      </span>
                      <span className="bars__value numeric">
                        {t('overview.packages.heldValue', { held: count(held), unit })}
                        <span className="bars__note">
                          {t('overview.packages.usedNote', { used: count(used), expired: count(expired) })}
                        </span>
                      </span>
                      <span className="chart-tip" role="presentation">
                        {description}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ol>
            <div className="chart__legend">
              {(['used', 'expired', 'held'] as const).map((part) => (
                <span key={part}>
                  <span className="chart__key" style={{ background: PARTS[part] }} />
                  {t(`overview.packages.legend.${part}`)}
                </span>
              ))}
            </div>
          </section>
        ) : null}

        <section className="panel">
          <div className="panel__heading">
            <h2>{t('overview.packages.expiring')}</h2>
            <p className="muted">{t('overview.packages.expiringNote')}</p>
          </div>
          <RankedBars rows={expiring} />
        </section>
      </div>
    </div>
  );
}
